import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  InvoiceListResponse,
  InvoiceResponse,
  SendEmailRequestPayload,
  SendEmailResponse,
  CheckoutDetails,
  APIResponse,
  CheckoutResponse,
  RetryPaymentPayload,
} from '@models/Invoice';

// Define the state type
export interface InvoiceState {
  invoiceList: InvoiceListResponse | null;
  loading: boolean;
  error: string | null;
  isSuccess: boolean;
  refreshInvoiceDetails: boolean;
  updatedEmail: SendEmailResponse | null;
  sendEmailError: string | null;
  checkoutDetails: CheckoutResponse | null;
  sendEmailSuccess: boolean;
  sendEmailFailed: boolean;
}

// Initial state
const initialState: InvoiceState = {
  invoiceList: null,
  loading: false,
  error: null,
  isSuccess: false,
  refreshInvoiceDetails: false,
  updatedEmail: null,
  sendEmailSuccess: false,
  sendEmailFailed: false,
  sendEmailError: null,
  checkoutDetails: null,
};

// Create a slice
const invoiceSlice = createSlice({
  name: 'invoice',
  initialState,
  reducers: {
    // Actions to fetch invoice data
    fetchInvoiceListStart(state) {
      state.loading = true;
      state.error = null;
    },

    fetchInvoiceListSuccess(state, action: PayloadAction<InvoiceResponse>) {
      state.loading = false;
      state.invoiceList = action.payload.data ? action.payload.data : null;
      state.error = null;
      state.isSuccess = true;
    },

    fetchInvoiceListFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
      state.invoiceList = null;
      state.isSuccess = false;
    },

    refreshInvoiceDetailStart(state, action: PayloadAction<boolean>) {
      state.refreshInvoiceDetails = action.payload;
    },

    resetInvoiceDetailsStart(state) {
      state.invoiceList = null;
      state.loading = false;
      state.error = null;
    },

    postMailDocumentStart(state, _action: PayloadAction<SendEmailRequestPayload>) {
      state.loading = true;
      state.error = null;
    },

    postMailDocumentSuccess(state, action: PayloadAction<SendEmailResponse>) {
      state.loading = false;
      state.sendEmailSuccess = true;
      state.sendEmailFailed = false;
      state.updatedEmail = action.payload;
    },

    postMailDocumentFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.sendEmailFailed = true;
      state.sendEmailSuccess = false;
      state.sendEmailError = action.payload;
    },

    resetMailUpdateDetails(state) {
      state.updatedEmail = null;
      state.sendEmailFailed = false;
      state.sendEmailSuccess = false;
    },

    // Actions to checkout details
    postCheckoutDetailsStart(state, _action: PayloadAction<CheckoutDetails>) {
      state.loading = true;
      state.error = null;
    },

    postCheckoutDetailsSuccess(state, action: PayloadAction<APIResponse<CheckoutResponse>>) {
      state.loading = false;
      state.checkoutDetails = action.payload.data;
    },

    postCheckoutDetailsFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    // Actions to retry payment
    postRetryPaymentStart(state, _action: PayloadAction<RetryPaymentPayload>) {
      state.loading = true;
      state.error = null;
    },

    postRetryPaymentSuccess(state, action: PayloadAction<APIResponse<CheckoutResponse>>) {
      state.loading = false;
      state.checkoutDetails = action.payload.data;
    },

    postRetryPaymentFailure(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },
  },
});

// Export actions
export const {
  fetchInvoiceListStart,
  fetchInvoiceListSuccess,
  fetchInvoiceListFailure,
  resetInvoiceDetailsStart,
  refreshInvoiceDetailStart,
  postMailDocumentFailure,
  postMailDocumentSuccess,
  postMailDocumentStart,
  postCheckoutDetailsStart,
  postCheckoutDetailsSuccess,
  postCheckoutDetailsFailure,
  resetMailUpdateDetails,
  postRetryPaymentFailure,
  postRetryPaymentStart,
  postRetryPaymentSuccess,
} = invoiceSlice.actions;

// Export reducer to be added to the store
export default invoiceSlice.reducer;
